import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from './ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { ChevronDown, Plus, Check, Loader2, Pencil } from 'lucide-react';
import { JobIcon } from '../lib/jobIcon';
import { toast } from 'sonner';

// A person can have several jobs (project phases / repeat installs). Each job is
// its own customer record sharing the contact details. This switcher lets staff
// jump between a person's jobs and spin up a new one.
export const JobsSwitcher = ({ customerId }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'rename'
  const [renameJobId, setRenameJobId] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customersAPI.listJobs(customerId);
      setJobs(res.data?.jobs || []);
    } catch (e) {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const current = jobs.find((j) => j.is_current);
  const currentLabel = current?.job_label || 'This job';

  const openCreate = () => {
    setDialogMode('create');
    setRenameJobId(null);
    setNewLabel('');
    setDialogOpen(true);
  };

  const openRename = (job) => {
    setDialogMode('rename');
    setRenameJobId(job.id);
    setNewLabel(job.job_label || '');
    setDialogOpen(true);
  };

  const submitDialog = async () => {
    if (!newLabel.trim()) { toast.error('Give the project a name'); return; }
    setCreating(true);
    try {
      if (dialogMode === 'rename') {
        await customersAPI.renameJob(renameJobId, { job_label: newLabel.trim() });
        toast.success('Project renamed');
        setDialogOpen(false);
        await load();
      } else {
        const res = await customersAPI.createJob(customerId, { job_label: newLabel.trim() });
        toast.success(`Project "${newLabel.trim()}" created`);
        setDialogOpen(false);
        navigate(`/customers/${res.data.id}/edit`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return null;

  return (
    <div className="flex items-center gap-2" data-testid="jobs-switcher">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 gap-2 border-slate-300" data-testid="jobs-switcher-trigger">
            <span className="text-amber-600"><JobIcon job={current} className="w-4 h-4" /></span>
            <span className="max-w-[180px] truncate">{currentLabel}</span>
            {jobs.length > 1 && (
              <span className="text-xs text-slate-400">({jobs.length} jobs)</span>
            )}
            <ChevronDown className="w-4 h-4 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel>Jobs for this customer</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {jobs.map((j) => (
            <DropdownMenuItem
              key={j.id}
              className="flex items-start gap-2 cursor-pointer"
              onClick={() => { if (!j.is_current) navigate(`/customers/${j.id}`); }}
              data-testid={`jobs-switcher-item-${j.id}`}
            >
              <div className="w-4 pt-0.5">
                {j.is_current && <Check className="w-4 h-4 text-emerald-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 truncate flex items-center gap-1.5">
                  <span className="text-amber-600 flex-shrink-0"><JobIcon job={j} className="w-3.5 h-3.5" /></span>
                  <span className="truncate">{j.job_label}</span>
                </p>
                <p className="text-xs text-slate-500">{j.status_display}</p>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); openRename(j); }}
                className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700"
                data-testid={`jobs-switcher-rename-${j.id}`}
                title="Rename this project"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer text-amber-700 focus:text-amber-800"
            onClick={openCreate}
            data-testid="jobs-switcher-add"
          >
            <Plus className="w-4 h-4 mr-2" /> Add another project
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="new-job-dialog">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'rename' ? 'Rename project' : 'Add another project'}</DialogTitle>
            <DialogDescription>
              {dialogMode === 'rename'
                ? 'Give this project a clear name so it\'s easy to tell apart from the customer\'s other projects.'
                : 'Creates a separate project for this customer with its own equipment, contract, documents and invoices. Their contact details are copied across.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium text-slate-700">Project name</label>
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. EV Charger 2026, Phase 2 — Battery"
              className="mt-1"
              data-testid="new-job-label-input"
              onKeyDown={(e) => { if (e.key === 'Enter') submitDialog(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="new-job-cancel">Cancel</Button>
            <Button onClick={submitDialog} disabled={creating} className="bg-amber-600 hover:bg-amber-700" data-testid="new-job-create">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (dialogMode === 'rename' ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
              {dialogMode === 'rename' ? 'Save name' : 'Create project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobsSwitcher;
